import React, { useState, useRef, useEffect } from "react";
import { FaPaperclip } from "react-icons/fa";
import translations from "../translation"; // Import translations

const TextEditor = ({ setContent, initialValue, language }) => {
  const [attachment, setAttachment] = useState(null);
  const contentRef = useRef(null); // Reference for contentEditable div

  useEffect(() => {
    if (contentRef.current && initialValue) {
      contentRef.current.innerHTML = initialValue;
      setContent((prevContent) => ({
        ...prevContent,
        text: initialValue,
      })); // Initialize content state
      moveCaretToEnd(contentRef.current);
    }
  }, [initialValue]);

  const translate = (key) => translations[language][key] || key; // Translation function
  const detectDirection = (text) => {
    // Check for Arabic and other RTL characters
    const rtlRegex = /[\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/;
    return rtlRegex.test(text) ? "rtl" : "ltr";
  };
  const handleInput = () => {
    if (contentRef.current) {
      const content = contentRef.current.innerHTML.trim();
      const direction = detectDirection(content); // Detect direction dynamically

      setContent((prevContent) => ({
        ...prevContent,
        text: content, // Update text
        direction, // Store detected direction
      }));
    }
  };
  const moveCaretToEnd = (element) => {
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(element);
    range.collapse(false); // Move caret to the end
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const applyCommand = (command, value = null) => {
    document.execCommand(command, false, value);
  };

  const handleAttachment = (event) => {
    const file = event.target.files[0];
    if (file && (file.type === "application/pdf" || file.type.startsWith("image/"))) {
      setAttachment(file); // Save locally in TextEditor
      setContent((prevContent) => ({
        ...prevContent,
        attachment: file, // Pass attachment to parent
      }));
    } else {
      alert(translate("attachmentError"));
    }
  };

  const isRTL = () => {
    // Add logic to detect if the language is an RTL language
    return ["ar", "he", "fa", "ur"].includes(language); // Add other RTL languages as needed
  };

  return (
    <div className="text-editor-container">
      <label className="form-label mb-2">{translate("editorLabel")}</label>

      {/* Toolbar */}
      <div className="toolbar d-flex flex-wrap p-2 border bg-light">
        <button
          type="button"
          className="btn btn-outline-secondary me-2"
          onClick={() => applyCommand("bold")}
        >
          <b>{translate("bold")}</b>
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary me-2"
          onClick={() => applyCommand("italic")}
        >
          <i>{translate("italic")}</i>
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary me-2"
          onClick={() => applyCommand("underline")}
        >
          <u>{translate("underline")}</u>
        </button>

        <select
          className="form-select me-2"
          onChange={(e) => applyCommand("formatBlock", e.target.value)}
          defaultValue="normal"
        >
          <option value="normal">{translate("normalText")}</option>
          <option value="h1">{translate("heading1")}</option>
          <option value="h2">{translate("heading2")}</option>
          <option value="h3">{translate("heading3")}</option>
        </select>

        <select
          className="form-select me-2"
          onChange={(e) => applyCommand("fontSize", e.target.value)}
          defaultValue="3"
        >
          <option value="1">{translate("fontSmall")}</option>
          <option value="3">{translate("fontNormal")}</option>
          <option value="5">{translate("fontLarge")}</option>
          <option value="7">{translate("fontExtraLarge")}</option>
        </select>

        <label className="btn btn-outline-secondary d-flex align-items-center">
          <FaPaperclip className="me-2" />
          {translate("attach")}
          <input
            type="file"
            accept="application/pdf, image/*"
            onChange={handleAttachment}
            className="d-none"
          />
        </label>
      </div>

      {/* Editable Content Area */}
      <div
        ref={contentRef}
        contentEditable
        onInput={handleInput}
        onFocus={() => moveCaretToEnd(contentRef.current)} // Ensure caret is at the end on focus
        className="content-editable border p-3"
        style={{
          minHeight: "150px",
          backgroundColor: "#fff",
          direction: detectDirection(contentRef.current?.innerHTML || "") || "ltr", // Dynamically set direction
          textAlign: detectDirection(contentRef.current?.innerHTML || "") === "rtl" ? "right" : "left", // Align text accordingly
        }}
        placeholder={translate("editorPlaceholder")}
      ></div>

      {/* Attachment Display */}
      {attachment && (
        <div className="mt-3 p-2 border rounded bg-light">
          <p className="my-1">
            <strong>{translate("attachedFile")}:</strong> {attachment.name}
          </p>
        </div>
      )}
    </div>
  );
};

export default TextEditor;
